/**
 * Order Summary Component
 */

import { useCart, useShop } from '@/context';
import type { ShippingMethod } from '@/types';
import styles from './OrderSummary.module.css';

interface OrderSummaryProps {
  shippingMethod?: ShippingMethod | null;
  orderTotal?: number | null;
}

export function OrderSummary({ shippingMethod, orderTotal }: OrderSummaryProps = {}) {
  const { cart, isEmpty } = useCart();
  const { formatPrice, t } = useShop();

  if (isEmpty) {
    return (
      <div className={styles.container}>
        <p className={styles.empty}>{t('emptyCart')}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Votre commande</h3>

      <ul className={styles.items}>
        {cart.items.map((item) => (
          <li key={item.key} className={styles.item}>
            <div className={styles.itemImage}>
              <img src={item.image} alt={item.name} />
              <span className={styles.quantity}>{item.quantity}</span>
            </div>
            <div className={styles.itemDetails}>
              <span className={styles.itemName}>{item.name}</span>
              {item.variation_attributes &&
                Object.entries(item.variation_attributes).length > 0 && (
                  <span className={styles.itemVariation}>
                    {Object.values(item.variation_attributes).join(' / ')}
                  </span>
                )}
              {item.meta ? (
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                  {item.meta.options && Array.isArray(item.meta.options) ? (
                    item.meta.options.map((opt: any, idx: number) => (
                      <div key={idx} style={{ marginBottom: idx < item.meta!.options.length - 1 ? '6px' : 0 }}>
                        <strong>{opt.option_name || 'Personnalisation'}</strong>
                        <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '2px 0' }}>
                          {opt.initials && <li>Initiales: {opt.initials}</li>}
                          {opt.names && <li>Prénoms: {opt.names}</li>}
                          {opt.date && <li>Date: {opt.date}</li>}
                          {opt.message && <li>Message: {opt.message}</li>}
                          {opt.color && <li>Couleur: {opt.color}</li>}
                          {opt.font && <li>Police: {opt.font}</li>}
                          {opt.location && <li>Emplacement: {opt.location}</li>}
                        </ul>
                      </div>
                    ))
                  ) : (
                    <>
                      <strong>{item.meta.option_name || 'Personnalisation'}</strong>
                      <ul style={{ paddingLeft: '0', listStyle: 'none', margin: '2px 0' }}>
                        {item.meta.initials && <li>Initiales: {item.meta.initials}</li>}
                        {item.meta.names && <li>Prénoms: {item.meta.names}</li>}
                        {item.meta.date && <li>Date: {item.meta.date}</li>}
                        {item.meta.message && <li>Message: {item.meta.message}</li>}
                        {item.meta.color && <li>Couleur: {item.meta.color}</li>}
                        {item.meta.font && <li>Police: {item.meta.font}</li>}
                        {item.meta.location && <li>Emplacement: {item.meta.location}</li>}
                      </ul>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.85em', color: '#999', marginTop: '4px' }}>
                  Sans personnalisation
                </div>
              )}
            </div>
            <span className={styles.itemPrice}>
              {formatPrice(item.price * item.quantity)}
            </span>
          </li>
        ))}
      </ul>

      <div className={styles.totals}>
        <div className={styles.row}>
          <span>{t('subtotal')}</span>
          <span>{formatPrice(cart.totals.subtotal)}</span>
        </div>

        {shippingMethod && (
          <div className={styles.row}>
            <span>{shippingMethod.label}</span>
            <span>{shippingMethod.price === 0 ? 'Gratuit' : formatPrice(shippingMethod.price)}</span>
          </div>
        )}

        {cart.totals.tax > 0 && (
          <div className={styles.row}>
            <span>TVA</span>
            <span>{formatPrice(cart.totals.tax)}</span>
          </div>
        )}

        <div className={`${styles.row} ${styles.total}`}>
          <span>{t('total')}</span>
          <span className={styles.totalAmount}>
            {formatPrice(orderTotal ?? (cart.totals.total + (shippingMethod?.price ?? 0)))}
          </span>
        </div>
      </div>
    </div>
  );
}
